import { useQuery } from '@apollo/client';
import { READ_SALES_ORDER_STATUS } from 'graphql/queries/readSalesOrderStatus';
import { useEffect, useState } from 'react';
import {
  PaymentStatus,
  Query,
  QueryReadSalesOrderArgs,
  SalesOrderStatus,
} from 'types/generatedSchemaTypes';

type PollSalesOrderResult = {
  currentPaymentStatus: PaymentStatus | null | undefined;
  currentOrderStatus: SalesOrderStatus | null | undefined;
  lastPollingCompleted: boolean;
};

const usePollSalesOrderPaymentStatus = (
  salesOrderId?: string,
  updateType?: SalesOrderStatus | null,
  initialOrderStatus?: SalesOrderStatus | null,
  skip: boolean = false
): PollSalesOrderResult => {
  const [lastPollingCompleted, setLastPollingCompleted] = useState(true);

  const { data, startPolling, stopPolling } = useQuery<
    Query,
    QueryReadSalesOrderArgs
  >(READ_SALES_ORDER_STATUS, {
    variables: { salesOrderId: salesOrderId ?? '' },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !salesOrderId || skip,
  });

  const currentPaymentStatus = data?.readSalesOrder?.paymentState;
  const currentOrderStatus = data?.readSalesOrder?.orderStatus;

  // Determine if we've reached a terminal state
  const shouldStopPolling = determineIfPollingShouldStop(
    salesOrderId,
    updateType,
    currentOrderStatus,
    currentPaymentStatus,
    initialOrderStatus
  );

  useEffect(() => {
    if (skip || !salesOrderId) {
      return;
    }

    if (!shouldStopPolling) {
      startPolling(1000); // Poll every 1 second
      setLastPollingCompleted(false);
    } else {
      stopPolling();
      setLastPollingCompleted(true);
    }

    return () => {
      stopPolling();
      setLastPollingCompleted(true);
    };
  }, [salesOrderId, skip, shouldStopPolling, startPolling, stopPolling]);

  return {
    currentPaymentStatus: currentPaymentStatus,
    currentOrderStatus: currentOrderStatus ?? initialOrderStatus,
    lastPollingCompleted,
  };
};

const determineIfPollingShouldStop = (
  salesOrderId?: string,
  updateType?: SalesOrderStatus | null,
  currentOrderStatus?: SalesOrderStatus | null,
  currentPaymentStatus?: PaymentStatus | null,
  initialOrderStatus?: SalesOrderStatus | null
): boolean => {
  // Stop polling if no salesOrderId is provided
  if (!salesOrderId) {
    return true;
  }

  const isDraft = currentOrderStatus === 'DRAFT';
  const isActive = currentOrderStatus === 'ACTIVE';
  const isFailed = currentOrderStatus === 'FAILED';
  const isActivating = currentOrderStatus === 'ACTIVATING';
  const isWaitingForActivation =
    currentOrderStatus === 'WAITING_FOR_ACTIVATION' ||
    currentOrderStatus === 'WAITING_FOR_CSR_APPROVAL';
  const isPaymentNotInitiated = currentPaymentStatus === 'NOT_INITIATED';
  const isPaymentSuccess = currentPaymentStatus === 'SUCCESS_PAY';

  // Terminal state 1: Initial draft state with payment not initiated
  if (initialOrderStatus === null && isDraft && isPaymentNotInitiated) {
    return true;
  }

  // Terminal state 2: For activation flow - stop when activating with successful payment or in terminal states
  if (updateType === 'ACTIVATING') {
    // Stop if payment succeeded and order is activating (faster path)
    if (isActivating && isPaymentSuccess) {
      return true;
    }
    // Stop if reached terminal states
    if (isWaitingForActivation || isActive || isFailed) {
      return true;
    }
  }

  // Continue polling in all other cases
  return false;
};

export default usePollSalesOrderPaymentStatus;
